import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import AccountHealthBadge from './AccountHealthBadge';
import type { AccountSummary, AccountSortBy, SortDirection } from '@/lib/api/accounts';

interface AccountsTableProps {
  accounts: AccountSummary[];
  isLoading: boolean;
  offset: number;
  sortBy: AccountSortBy;
  sortDirection: SortDirection;
  onSortChange: (sort_by: AccountSortBy, sort_direction: SortDirection) => void;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function SortableHeader({
  label,
  field,
  sortBy,
  sortDirection,
  onSortChange,
  alignRight = false,
}: {
  label: string;
  field: AccountSortBy;
  sortBy: AccountSortBy;
  sortDirection: SortDirection;
  onSortChange: (sort_by: AccountSortBy, sort_direction: SortDirection) => void;
  alignRight?: boolean;
}) {
  const isActive = sortBy === field;
  const Icon = !isActive ? ArrowUpDown : sortDirection === 'asc' ? ArrowUp : ArrowDown;

  const handleClick = () => {
    if (!isActive) {
      onSortChange(field, 'desc');
    } else {
      onSortChange(field, sortDirection === 'asc' ? 'desc' : 'asc');
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-1 ${alignRight ? 'justify-end w-full' : ''} ${isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
    >
      <span>{label}</span>
      <Icon className="h-3 w-3 opacity-70" />
    </button>
  );
}

const AccountsTable: React.FC<AccountsTableProps> = ({
  accounts,
  isLoading,
  sortBy,
  sortDirection,
  onSortChange,
}) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <p className="text-sm">No accounts yet.</p>
        <p className="mt-1 text-xs">
          Accounts appear once people from the same company domain interact with your agent.
        </p>
      </div>
    );
  }

  return (
    <Table aria-label="Accounts analytics table">
      <TableCaption className="sr-only">
        List of accounts with engagement, intent, and health details.
      </TableCaption>
      <TableHeader className="bg-transparent">
        <TableRow className="hover:bg-transparent">
          <TableHead className="text-[12px] font-normal text-muted-foreground">Company</TableHead>
          <TableHead className="text-right text-[12px] font-normal text-muted-foreground">
            <SortableHeader
              label="Users"
              field="total_users"
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              alignRight
            />
          </TableHead>
          <TableHead className="text-right text-[12px] font-normal text-muted-foreground">
            <SortableHeader
              label="Sessions"
              field="total_sessions"
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              alignRight
            />
          </TableHead>
          <TableHead className="hidden text-right text-[12px] font-normal text-muted-foreground lg:table-cell">
            <SortableHeader
              label="Intent"
              field="high_intent_users"
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              alignRight
            />
          </TableHead>
          <TableHead className="whitespace-nowrap text-[12px] font-normal text-muted-foreground">
            <SortableHeader
              label="Last seen"
              field="last_seen_at"
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
            />
          </TableHead>
          <TableHead className="hidden text-[12px] font-normal text-muted-foreground md:table-cell">
            <SortableHeader
              label="Health"
              field="health_band"
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
            />
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {accounts.map((account) => (
          <TableRow
            key={account.account_domain}
            className="h-10 cursor-pointer border-border/70 hover:bg-accent/50"
            onClick={() => navigate(`/accounts/${encodeURIComponent(account.account_domain)}`)}
          >
            <TableCell className="min-w-[260px]">
              <div className="min-w-0 space-y-1">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="truncate text-[13px] font-normal text-foreground">
                    {account.company_name || account.account_domain}
                  </div>
                  <div className="truncate text-[13px] text-muted-foreground underline decoration-border decoration-dotted underline-offset-4">
                    {account.account_domain}
                  </div>
                </div>
                <div className="md:hidden">
                  <AccountHealthBadge
                    band={account.health_band}
                    lastSeenAt={account.last_seen_at}
                    totalUsers={account.total_users}
                    highIntentUsers={account.high_intent_users}
                    totalSessions={account.total_sessions}
                  />
                </div>
                {account.enrichment_status === 'pending' && (
                  <div className="text-[10px] text-muted-foreground/70">Enrichment pending…</div>
                )}
                {account.enrichment_status === 'failed' && !account.company_name && (
                  <div className="text-[10px] text-muted-foreground/70">Company name unavailable</div>
                )}
              </div>
            </TableCell>
            <TableCell className="text-right text-[13px] tabular-nums">{account.total_users}</TableCell>
            <TableCell className="text-right text-[13px] tabular-nums">{account.total_sessions}</TableCell>
            <TableCell className="hidden text-right text-[13px] tabular-nums lg:table-cell">{account.high_intent_users}</TableCell>
            <TableCell className="whitespace-nowrap text-[13px] text-foreground">
              {formatDate(account.last_seen_at)}
            </TableCell>
            <TableCell className="hidden md:table-cell">
              <AccountHealthBadge
                band={account.health_band}
                lastSeenAt={account.last_seen_at}
                totalUsers={account.total_users}
                highIntentUsers={account.high_intent_users}
                totalSessions={account.total_sessions}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default AccountsTable;
